import { format, parseISO, differenceInCalendarDays, addDays } from 'date-fns'
import type { Tour, Activity } from '@/types'
import { getLocalDate } from '@/lib/timezones'

// Status drives the colored pill at the top-left of each tour card.
//   upcoming  → starts in the future
//   live      → today is between start and end (more than one day left)
//   wrapping  → today is the last or second-to-last day
//   past      → ended (only used in archived view)
export type TourStatus = 'upcoming' | 'live' | 'wrapping' | 'past'

export interface DayCell {
  date: string // YYYY-MM-DD
  dow: string  // 'Mo' | 'Tu' | ...
  num: number  // day of month
  isPast: boolean
  isToday: boolean
  isFuture: boolean
  isShow: boolean   // has at least one activity with venue_name
  isTravel: boolean // in range, but no show that day (and not today)
}

export interface TourState {
  status: TourStatus
  dayN: number       // 1-based current day inside the tour (clamped to [0..total])
  dayTotal: number
  progressPct: number
  cells: DayCell[]
  showDays: number
  travelDays: number
  nextActivity: Activity | null
}

// All comparisons happen in UTC days (YYYY-MM-DD strings) to avoid timezone
// drift — tour dates are stored as date-only strings, not timestamps.
function todayIso(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const DOW_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export function computeTourState(tour: Tour, activities: Activity[]): TourState {
  const start = parseISO(tour.fecha_inicio)
  const end = parseISO(tour.fecha_fin)
  const todayStr = todayIso()
  const today = parseISO(todayStr)

  const dayTotal = Math.max(1, differenceInCalendarDays(end, start) + 1)
  const elapsed = differenceInCalendarDays(today, start) // negative if upcoming
  const dayN = Math.min(dayTotal, Math.max(0, elapsed + 1))
  const progressPct = Math.round((dayN / dayTotal) * 100)

  // Bucket activities by their venue-local date so a flight that takes off
  // before midnight UTC but lands the next day shows up on the right day.
  const showDates = new Set<string>()
  for (const a of activities) {
    if (!a.venue_name) continue
    const localDate = getLocalDate(a.start_time_utc, a.venue_timezone)
    showDates.add(localDate)
  }

  const cells: DayCell[] = []
  for (let i = 0; i < dayTotal; i++) {
    const d = addDays(start, i)
    const iso = format(d, 'yyyy-MM-dd')
    const isPast = iso < todayStr
    const isToday = iso === todayStr
    const isFuture = iso > todayStr
    const isShow = showDates.has(iso)
    cells.push({
      date: iso,
      dow: DOW_SHORT[d.getDay()],
      num: d.getDate(),
      isPast,
      isToday,
      isFuture,
      isShow,
      isTravel: !isShow && !isToday,
    })
  }

  const showDays = cells.filter(c => c.isShow).length
  const travelDays = cells.filter(c => c.isTravel).length

  // Status: outside the range → upcoming/past; inside → live/wrapping based
  // on how close to the end we are.
  let status: TourStatus
  if (todayStr < tour.fecha_inicio) status = 'upcoming'
  else if (todayStr > tour.fecha_fin) status = 'past'
  else if (dayTotal - dayN <= 1) status = 'wrapping'
  else status = 'live'

  // Next future activity (earliest start_time_utc that's still ahead).
  const nowMs = Date.now()
  const nextActivity = activities
    .filter(a => new Date(a.start_time_utc).getTime() > nowMs)
    .sort((a, b) => a.start_time_utc.localeCompare(b.start_time_utc))[0] ?? null

  return { status, dayN, dayTotal, progressPct, cells, showDays, travelDays, nextActivity }
}

// Card accent color is deterministic per tour id so it doesn't shuffle when
// the list re-orders. Three rotating colors match the design.
export type TourAccent = 'violet' | 'amber' | 'teal'
const ACCENTS: TourAccent[] = ['violet', 'amber', 'teal']

export function tourAccent(tourId: string): TourAccent {
  let h = 0
  for (let i = 0; i < tourId.length; i++) h = (h * 31 + tourId.charCodeAt(i)) >>> 0
  return ACCENTS[h % ACCENTS.length]
}

// "APR 16 — APR 23 · 2026"
export function formatTourDates(tour: Tour): string {
  const start = parseISO(tour.fecha_inicio)
  const end = parseISO(tour.fecha_fin)
  const sameYear = start.getFullYear() === end.getFullYear()
  const startStr = format(start, 'MMM d').toUpperCase()
  const endStr = format(end, 'MMM d').toUpperCase()
  return sameYear
    ? `${startStr} — ${endStr} · ${end.getFullYear()}`
    : `${startStr} ${start.getFullYear()} — ${endStr} ${end.getFullYear()}`
}

// "Friday", "in 4h 18m", "today" — short countdown for the Up next block.
export function shortCountdown(targetIso: string): string {
  const target = new Date(targetIso).getTime()
  const diff = target - Date.now()
  if (diff <= 0) return 'now'
  const mins = Math.round(diff / 60_000)
  if (mins < 60) return `in ${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  if (hours < 24) return remMins === 0 ? `in ${hours}h` : `in ${hours}h ${remMins}m`
  const days = Math.floor(hours / 24)
  if (days < 7) {
    const weekday = format(new Date(targetIso), 'EEEE')
    return weekday
  }
  return format(new Date(targetIso), 'MMM d')
}
